import React from 'react';
import { Button } from '../components/Button';
import { BriefcaseIcon, BabyIcon, MountainIcon, UserIcon, ActivityIcon, ShieldCheckIcon, MessageSquareHeartIcon } from 'lucide-react';
interface AboutPageProps {
  setPage: (page: 'home' | 'about' | 'contact') => void;
}
export function AboutPage({
  setPage
}: AboutPageProps) {
  return <div className="flex flex-col w-full" data-id="element-53">
      {/* Intro Section - Left aligned with accent bar */}
      <section className="bg-cream py-20 md:py-32 relative overflow-hidden" data-id="element-54">
        {/* Decorative Element */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 text-[20rem] font-heading font-bold text-stone-200/50 leading-none select-none hidden lg:block pointer-events-none" data-id="element-55">
          01
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-56">
          <div className="max-w-3xl flex" data-id="element-57">
            <div className="w-1.5 bg-accent mr-8 flex-shrink-0 rounded-full" data-id="element-58"></div>
            <div data-id="element-59">
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-8" data-id="element-60">
                Why [CLINIC NAME]
              </h1>
              <p className="text-xl md:text-2xl text-charcoal leading-relaxed font-light" data-id="element-61">
                We believe that healthcare should be about helping you feel
                better in your daily life, not just managing lab numbers. Our
                philosophy is rooted in personalized, accessible care that fits
                into your lifestyle, empowering you to reclaim your energy,
                focus, and drive.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section - Asymmetric layout */}
      <section className="pb-24 pt-12 bg-white" data-id="element-62">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-63">
          <div className="relative max-w-4xl mx-auto" data-id="element-64">
            {/* Circular Image overlapping top */}
            <div className="w-48 h-48 md:w-56 md:h-56 rounded-full bg-stone-200 border-8 border-white shadow-xl flex items-center justify-center overflow-hidden mx-auto -mt-32 mb-12 relative z-20" data-id="element-65">
              <UserIcon className="h-20 w-20 text-stone-400" data-id="element-66" />
            </div>

            <div className="text-center px-4 md:px-12" data-id="element-67">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-8" data-id="element-68">
                Our Story
              </h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto" data-id="element-69">
                <p data-id="element-70">
                  [CLINIC NAME] was founded with a simple mission: to make
                  high-quality men's wellness care accessible, straightforward,
                  and effective. We saw too many men accepting fatigue and low
                  drive as "just a part of getting older."
                </p>
                <p data-id="element-71">
                  By leveraging telehealth, we remove the friction of
                  traditional clinics. No awkward waiting rooms, no taking half
                  a day off work. Just expert care, tailored to you, delivered
                  securely to your home.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve Section - 2x2 grid, alternating backgrounds */}
      <section className="py-24 bg-primary text-white" data-id="element-72">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-73">
          <div className="mb-16" data-id="element-74">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4" data-id="element-75">
              Men Like You
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl" data-id="element-76">
              Our patients come from all walks of life, but they share one goal:
              wanting to perform at their best.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-id="element-77">
            {[{
            icon: BriefcaseIcon,
            title: 'Busy Professionals',
            desc: 'Need sustained focus and energy for long days.',
            bg: 'bg-white text-primary'
          }, {
            icon: BabyIcon,
            title: 'Active Dads',
            desc: 'Want the stamina to keep up with their kids.',
            bg: 'bg-cream text-primary'
          }, {
            icon: MountainIcon,
            title: 'Weekend Warriors',
            desc: 'Looking to improve recovery and athletic performance.',
            bg: 'bg-cream text-primary'
          }, {
            icon: UserIcon,
            title: 'Men Over 40',
            desc: 'Refusing to let age dictate their vitality.',
            bg: 'bg-white text-primary'
          }].map((segment, i) => <div key={i} className={`${segment.bg} rounded-2xl p-10 shadow-lg flex items-start group`} data-id="element-78">
                <div className="bg-primary/10 p-4 rounded-xl mr-6 group-hover:bg-accent/20 transition-colors" data-id="element-79">
                  <segment.icon className="h-8 w-8 text-primary" data-id="element-80" />
                </div>
                <div data-id="element-81">
                  <h3 className="font-heading text-2xl font-bold mb-3" data-id="element-82">
                    {segment.title}
                  </h3>
                  <p className="text-gray-600 text-lg" data-id="element-83">{segment.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Treatments & Support Section - Horizontal rows */}
      <section className="py-24 bg-white" data-id="element-84">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-85">
          <div className="text-center mb-16" data-id="element-86">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4" data-id="element-87">
              Treatments & Support
            </h2>
            <p className="text-lg text-gray-600" data-id="element-88">
              Comprehensive care designed around your unique needs.
            </p>
          </div>

          <div className="space-y-0" data-id="element-89">
            {/* Row 1 */}
            <div className="flex flex-col sm:flex-row items-start py-10 border-b border-gray-200" data-id="element-90">
              <div className="bg-cream p-5 rounded-2xl sm:mr-8 mb-6 sm:mb-0 flex-shrink-0" data-id="element-91">
                <ActivityIcon className="h-10 w-10 text-accent" data-id="element-92" />
              </div>
              <div data-id="element-93">
                <h3 className="font-heading text-2xl font-bold text-primary mb-3" data-id="element-94">
                  Hormone Evaluation & Review
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed" data-id="element-95">
                  A thorough review of your symptoms and comprehensive lab work
                  to understand exactly what your body needs.
                </p>
              </div>
            </div>

            {/* Row 2 */}
            <div className="flex flex-col sm:flex-row items-start py-10 border-b border-gray-200" data-id="element-96">
              <div className="bg-cream p-5 rounded-2xl sm:mr-8 mb-6 sm:mb-0 flex-shrink-0" data-id="element-97">
                <ShieldCheckIcon className="h-10 w-10 text-accent" data-id="element-98" />
              </div>
              <div data-id="element-99">
                <h3 className="font-heading text-2xl font-bold text-primary mb-3" data-id="element-100">
                  Ongoing TRT Management
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed" data-id="element-101">
                  Safe, closely monitored testosterone replacement therapy
                  tailored to optimize your levels and minimize side effects.
                </p>
              </div>
            </div>

            {/* Row 3 */}
            <div className="flex flex-col sm:flex-row items-start py-10" data-id="element-102">
              <div className="bg-cream p-5 rounded-2xl sm:mr-8 mb-6 sm:mb-0 flex-shrink-0" data-id="element-103">
                <MessageSquareHeartIcon className="h-10 w-10 text-accent" data-id="element-104" />
              </div>
              <div data-id="element-105">
                <h3 className="font-heading text-2xl font-bold text-primary mb-3" data-id="element-106">
                  Lifestyle & Follow-Up Support
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed" data-id="element-107">
                  Continuous guidance and adjustments to ensure your treatment
                  aligns with your evolving lifestyle and goals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect Timeline - Horizontal on desktop */}
      <section className="py-24 bg-cream overflow-hidden" data-id="element-108">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-109">
          <div className="text-center mb-20" data-id="element-110">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4" data-id="element-111">
              What to Expect
            </h2>
          </div>

          {/* Desktop Horizontal Timeline */}
          <div className="hidden md:block relative" data-id="element-112">
            <div className="absolute top-6 left-0 right-0 h-1 bg-stone-300" data-id="element-113"></div>
            <div className="grid grid-cols-4 gap-8 relative z-10" data-id="element-114">
              {[{
              step: 1,
              title: 'Book Your Consultation',
              desc: 'Schedule a time that works for you using our simple online system.'
            }, {
              step: 2,
              title: 'Meet Your Provider (Telehealth)',
              desc: 'Discuss your symptoms and goals in a secure, confidential video call.'
            }, {
              step: 3,
              title: 'Start Your Personalized Plan',
              desc: 'If treatment is right for you, medications are shipped directly to your door.'
            }, {
              step: 4,
              title: 'Feel the Difference',
              desc: 'Experience renewed energy and drive, with our team supporting you every step of the way.'
            }].map((item, i) => <div key={i} className="relative pt-16" data-id="element-115">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-accent text-primary font-bold flex items-center justify-center shadow-md text-xl border-4 border-cream" data-id="element-116">
                    {item.step}
                  </div>
                  <div className="text-center px-2" data-id="element-117">
                    <h3 className="font-heading text-xl font-bold text-primary mb-3" data-id="element-118">
                      {item.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-119">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Mobile Vertical Timeline */}
          <div className="md:hidden relative border-l-2 border-accent/30 ml-4 space-y-12" data-id="element-120">
            {[{
            step: 1,
            title: 'Book Your Consultation',
            desc: 'Schedule a time that works for you using our simple online system.'
          }, {
            step: 2,
            title: 'Meet Your Provider (Telehealth)',
            desc: 'Discuss your symptoms and goals in a secure, confidential video call.'
          }, {
            step: 3,
            title: 'Start Your Personalized Plan',
            desc: 'If treatment is right for you, medications are shipped directly to your door.'
          }, {
            step: 4,
            title: 'Feel the Difference',
            desc: 'Experience renewed energy and drive, with our team supporting you every step of the way.'
          }].map((item, i) => <div key={i} className="relative pl-8" data-id="element-121">
                <div className="absolute -left-[17px] top-1 h-8 w-8 rounded-full bg-accent text-primary font-bold flex items-center justify-center shadow-md" data-id="element-122">
                  {item.step}
                </div>
                <h3 className="font-heading text-xl font-bold text-primary mb-2" data-id="element-123">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-lg" data-id="element-124">{item.desc}</p>
              </div>)}
          </div>

          <div className="mt-20 text-center" data-id="element-125">
            <Button onClick={() => setPage('contact')} variant="primary" className="text-lg px-10 py-4 shadow-md" data-id="element-126">
              Schedule your tele-health appointment
            </Button>
          </div>
        </div>
      </section>
    </div>;
}